// Components/Pages/Admin/Resources.jsx
import { useState, useEffect } from "react";
import { api } from "./api";
import { FaUpload, FaTrash, FaDownload, FaFilePdf, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: ""
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const data = await api.getResources();
      setResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await api.deleteResource(id);
        await fetchResources();
        toast.success('Resource deleted successfully!');
      } catch (error) {
        console.error('Error deleting resource:', error);
        toast.error('Failed to delete resource');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file is PDF
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
    // Auto-fill title from file name
    setFormData({
      title: file.name.replace(/\.[^/.]+$/, ""),
      description: ""
    });
    setShowModal(true);
    e.target.value = '';
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitUpload = async () => {
    // Validate title
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Validate description (max 20 words)
    const wordCount = formData.description.trim().split(/\s+/).length;
    if (formData.description.trim() && wordCount > 20) {
      toast.error('Description cannot exceed 20 words');
      return;
    }

    if (!selectedFile) {
      toast.error('No file selected');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Uploading PDF...');
    
    try {
      const result = await api.uploadFile(selectedFile);
      
      // Create resource entry with uploaded file
      await api.createResource({
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: "PDF",
        file_type: "PDF",
        file_size: (selectedFile.size / (1024 * 1024)).toFixed(2) + ' MB',
        file_url: result.filepath,
        icon: "📄",
        date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
      
      toast.update(toastId, {
        render: 'PDF uploaded successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
      setShowModal(false);
      setSelectedFile(null);
      setFormData({ title: "", description: "" });
      await fetchResources();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.update(toastId, {
        render: 'Failed to upload PDF',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFile(null);
    setFormData({ title: "", description: "" });
  };

  // Count words in description
  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Format file size
  const formatFileSize = (size) => {
    if (!size) return 'N/A';
    return size;
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      {/* Upload Button */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-primaryBlue">Upload PDF</h3>
              <p className="text-sm text-gray-500">Upload a PDF document to add to resources</p>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <button 
                className="bg-primaryGold text-primaryBlue px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all flex items-center gap-2"
                disabled={uploading}
              >
                <FaUpload />
                {uploading ? 'Uploading...' : 'Choose PDF'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primaryBlue">Upload PDF</h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File name display */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">File:</span> {selectedFile?.name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Size:</span> {(selectedFile?.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>

              {/* Title field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Enter resource title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                />
              </div>

              {/* Description field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (max 20 words)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Brief description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                />
                <div className={`text-xs mt-1 ${getWordCount(formData.description) > 20 ? 'text-red-500' : 'text-gray-400'}`}>
                  {getWordCount(formData.description)} / 20 words
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleSubmitUpload}
                disabled={uploading}
                className="flex-1 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resources List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-primaryBlue">Resources</h2>
        </div>
        
        {resources.length === 0 ? (
          <div className="text-center py-12">
            <FaFilePdf className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No resources uploaded yet.</p>
            <p className="text-gray-400 text-sm mt-1">Upload a PDF to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {resources.map((resource) => (
              <div key={resource.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">{resource.icon || '📄'}</div>
                  <div>
                    <h4 className="font-medium text-gray-800">{resource.title}</h4>
                    {resource.description && (
                      <p className="text-sm text-gray-500">{resource.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{resource.file_type || 'PDF'}</span>
                      <span>•</span>
                      <span>{formatFileSize(resource.file_size)}</span>
                      <span>•</span>
                      <span>{resource.date || ''}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {resource.file_url && (
                    <a
                      href={`https://api.rigokenya.org${resource.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primaryGold hover:text-yellow-600 transition-colors p-2"
                      title="Download"
                    >
                      <FaDownload />
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(resource.id)}
                    className="text-red-500 hover:text-red-700 transition-colors p-2"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Resources;